<template>
  <div class="nr-editor" role="toolbar">
    <NrFormLabel v-if="label" :id="id" :class="{ 'required-label': required }"
      >{{ label }}
      <template v-if="hasTooltip" v-slot:tip>{{ tooltip }}</template>
    </NrFormLabel>
    <ul
      class="nr-editor__toolbar"
      :id="nrToolbarId"
      aria-label="Formatting options"
    >
      <li>
        <NrTooltip v-if="fontSize" label="Font sizes">
          <select class="ql-size">
            <option value="small">Small</option>
            <option selected>Normal</option>
            <option value="large">Large</option>
            <option value="huge">Huge</option>
          </select>
        </NrTooltip>
      </li>
      <li>
        <NrTooltip v-if="bold" label="Bold">
          <button
            type="button"
            aria-label="Bold button"
            :aria-controls="nrEditorId"
            class="ql-bold"
            @click="handleButtonClick('Bold')"
            @pointerdown.prevent
          ></button>
        </NrTooltip>
      </li>
      <li>
        <NrTooltip v-if="italic" label="Italic">
          <button
            type="button"
            aria-label="Italic button"
            :aria-controls="nrEditorId"
            class="ql-italic"
            @click="handleButtonClick('Italic')"
            @pointerdown.prevent
          ></button>
        </NrTooltip>
      </li>
      <li>
        <NrTooltip v-if="underline" label="Underline">
          <button
            type="button"
            aria-label="Underline button"
            :aria-controls="nrEditorId"
            class="ql-underline"
            @click="handleButtonClick('Underline')"
            @pointerdown.prevent
          ></button>
        </NrTooltip>
      </li>
      <li>
        <NrTooltip v-if="strike" label="Strike">
          <button
            type="button"
            aria-label="Strike button"
            :aria-controls="nrEditorId"
            class="ql-strike"
          ></button>
        </NrTooltip>
      </li>
      <li>
        <NrTooltip v-if="bulletList" label="Bullet list">
          <button
            type="button"
            aria-label="Bullet list button"
            :aria-controls="nrEditorId"
            class="ql-list"
            value="bullet"
          ></button>
        </NrTooltip>
      </li>
      <li>
        <NrTooltip v-if="orderedList" label="Ordered list">
          <button
            type="button"
            aria-label="Ordered list button"
            :aria-controls="nrEditorId"
            class="ql-list"
            value="ordered"
          ></button>
        </NrTooltip>
      </li>
      <li>
        <NrTooltip v-if="outdent" label="Decrease indentation">
          <button
            type="button"
            aria-label="Decrease indentation button"
            :aria-controls="nrEditorId"
            class="ql-indent"
            value="-1"
          ></button>
        </NrTooltip>
      </li>
      <li>
        <NrTooltip v-if="indent" label="Increase indentation">
          <button
            type="button"
            aria-label="Increase indentation button"
            :aria-controls="nrEditorId"
            class="ql-indent"
            value="+1"
          ></button>
        </NrTooltip>
      </li>
      <li>
        <NrTooltip v-if="link" label="Link">
          <button
            type="button"
            aria-label="Link button"
            :aria-controls="nrEditorId"
            class="ql-link"
          ></button>
        </NrTooltip>
      </li>
    </ul>
    <div :class="editorContainerClassnames">
      <div
        class="nr-editor__content"
        :id="nrEditorId"
        @keyup="validate($event)"
        @focusout="validate($event)"
      ></div>
    </div>

    <p v-if="showRequiredText" class="required-field">
      This field is required.
    </p>
  </div>
</template>

<script lang="ts">
import Quill from 'quill'
import NrTooltip from '../NrTooltip/NrTooltip.vue'
import NrFormLabel from '../form-elements/nr-form-label.vue'
import { uniqueId } from 'lodash'

const COMPONENT_NAME = 'nr-editor'
const QUILL_FORMATS = [
  'bold',
  'indent',
  'italic',
  'link',
  'list',
  'size',
  'strike',
  'underline'
]

interface Data {
  showRequiredText: boolean
  formatting: Map<string, boolean>
  editorContent: string
  nrEditorContainer: Element | null
  nrToolbarContainer: Element | null
  nrEditorId: string
  nrToolbarId: string
}

export default {
  components: { NrTooltip, NrFormLabel },
  emits: ['update:modelValue'],
  props: {
    required: { default: false, type: Boolean },
    resizable: { default: true, type: Boolean },
    autofocus: { default: false, type: Boolean },
    rows: { default: 5, type: Number },
    placeholder: { default: '', type: String },
    modelValue: { default: '', type: String },
    bold: { default: true, type: Boolean },
    italic: { default: true, type: Boolean },
    underline: { default: true, type: Boolean },
    strike: { default: false, type: Boolean },
    orderedList: { default: false, type: Boolean },
    bulletList: { default: false, type: Boolean },
    indent: { default: false, type: Boolean },
    outdent: { default: false, type: Boolean },
    link: { default: false, type: Boolean },
    fontSize: { default: false, type: Boolean },
    label: { type: String },
    hasTooltip: { type: Boolean, default: false },
    tooltip: { type: String, default: null }
  },
  data(): Data {
    return {
      formatting: new Map(),
      editorContent: '',
      nrEditorContainer: null,
      nrToolbarContainer: null,
      showRequiredText: false,
      nrEditorId: uniqueId(`${COMPONENT_NAME}-editor-`),
      nrToolbarId: uniqueId(`${COMPONENT_NAME}-toolbar-`)
    }
  },
  mounted() {
    this.nrEditorContainer = document.querySelector(`#${this.nrEditorId}`)
    this.nrToolbarContainer = document.querySelector(`#${this.nrToolbarId}`)

    this.initEditor()
  },
  beforeDestroy(): void {
    this.deInitEditor()
  },
  computed: {
    id() {
      return uniqueId(`${COMPONENT_NAME}-`)
    },
    editorContainerClassnames() {
      return {
        'nr-editor__content-container': true,
        'nr-editor__content-container--resizable': this.resizable
      }
    },
    editorHeight() {
      return `${Math.ceil(1.5 * this.rows)}em`
    },
    findQuill() {
      return Quill.find(this.nrEditorContainer as HTMLElement)
    }
  },
  methods: {
    async deInitEditor(): Promise<void> {
      if (this.findQuill) {
        this.findQuill.off('text-change')

        if (this.nrEditorContainer && this.nrToolbarContainer) {
          this.nrToolbarContainer.innerHTML = ''
          ;[...this.nrToolbarContainer.classList]
            .filter((classname) => /^ql-/i.test(classname))
            .forEach((classname) => {
              this.nrToolbarContainer?.classList.remove(classname)
            })

          this.nrEditorContainer.innerHTML = ''
          ;[...this.nrEditorContainer.classList]
            .filter((classname) => /^ql-/i.test(classname))
            .forEach((classname) => {
              this.nrEditorContainer?.classList.remove(classname)
            })
        }
      }
    },
    async initEditor(): Promise<void> {
      const showToolbar = !![
        this.bold,
        this.italic,
        this.underline,
        this.strike,
        this.orderedList,
        this.bulletList,
        this.indent,
        this.outdent,
        this.link,
        this.fontSize
      ].filter(Boolean).length

      if (!this.nrEditorContainer) return

      const editor = new Quill(this.nrEditorContainer as HTMLElement, {
        theme: 'snow',
        bounds: this.nrEditorContainer as HTMLElement,
        placeholder: this.placeholder,
        modules: {
          keyboard: {
            bindings: {
              tab: {
                key: 9,
                handler: function () {
                  return true
                }
              }
            }
          },
          clipboard: { matchVisual: false },
          toolbar: showToolbar ? this.nrToolbarContainer : false
        },
        formats: QUILL_FORMATS
      })

      editor.clipboard.dangerouslyPasteHTML(this.modelValue)
      this.handleOnChange()
      this.setupEditorAttributes()
      editor.on('text-change', this.handleOnChange.bind(this))
    },

    setupEditorAttributes(): void {
      const editorInputContainer = document.getElementById(
        `${this.nrEditorId}`
      ) as HTMLElement
      const editor = editorInputContainer.children[0]
      if (!editor) return
      editor.setAttribute('role', 'textbox')
      editor.setAttribute('aria-required', `${this.required}`)
      editor.setAttribute('required', `${this.required}`)
      editor.setAttribute('aria-label', this.label ? this.label : 'Editor')
      editor.setAttribute('aria-labelledby', this.label ? this.id : '')
    },

    validate(event: Event) {
      const target = event.target as HTMLInputElement
      this.showRequiredText = this.required && !target.textContent
    },

    handleOnChange(): void {
      let content = this.findQuill?.getSemanticHTML().trim()
      if (!content || content === '<p></p>') {
        content = ''
      }

      this.editorContent = content

      this.$emit('update:modelValue', this.editorContent)
    },

    handleButtonClick(label: string) {
      this.formatting.set(label, !this.formatting.get(label))
      this.$emit('button-triggered', label, this.formatting.get(label))
    }
  },
  watch: {
    modelValue(newValue) {
      if (newValue === this.editorContent) {
        return
      }

      this.findQuill?.clipboard.dangerouslyPasteHTML(newValue)
    }
  }
}
</script>

<style lang="scss">
@import 'quill/dist/quill.snow.css';
@import '../../styles/tokens/tokens.scss';

.nr-editor {
  display: flex;
  flex-direction: column;

  .nr-form-label.required-label::after {
    content: ' *';
    padding-left: $spacing-4;
  }

  .required-field {
    font-size: $font-size-1;
    color: $color-red-100;
    margin-top: $spacing-8;
  }
}

.nr-editor__toolbar li {
  display: inline-flex;
}

.nr-editor__content-container {
  overflow-y: auto;
  border: 1px solid #cccccc;
  border-top: none;
  flex: 1 1 auto;
  height: v-bind(editorHeight);
  min-height: v-bind(editorHeight);

  &--resizable {
    resize: vertical;
  }
}

.nr-editor__content {
  flex: 1 1 100%;
  min-height: 100%;
}

.ql-container {
  font-size: 1rem;
}

.ql-editor {
  overflow: auto;
  background-color: #fff;
}

.ql-container.ql-snow {
  border: none;
}

.ql-snow.ql-toolbar {
  .ql-size.ql-picker {
    border: 1px solid #ccc;
  }

  button {
    &:focus,
    &:hover {
      background-color: rgba(15, 28, 112, 0.25);
      color: unset;

      .ql-stroke {
        stroke: currentColor;
      }
    }

    &.ql-active {
      background-color: #eee;
      color: unset;

      .ql-stroke {
        stroke: currentColor;
      }
    }
  }
}
</style>
